import {
  DateBetweenPicker,
  RelativeDatePicker,
  RelativeDatePickerMode,
  RelativeDatePresetMode,
} from '@ardoq/date-time-picker';
import { Flex } from '../atoms';
import { isString } from 'lodash';
import {
  DateValueKind,
  getDateValueKind,
  QueryBuilderFilterType,
} from '../../filterUtils';
import {
  FormattingOperator,
  Operator,
  QueryBuilderRuleValue,
  RelativeDateValue as RelativeDateValueType,
} from '@ardoq/api-types';
import { RelativeDateValue } from './RelativeDateValue';
import { parseDate } from '@ardoq/date-time';
import { type Locale } from '@ardoq/locale';
import { DEFAULT_ARDOQ_I18NLOCALE, t, type I18nLocale } from '@ardoq/i18n';

type RuleDatePickerInputProps = {
  value?: QueryBuilderRuleValue;
  operator: Operator | FormattingOperator;
  onValueChange: (value: QueryBuilderRuleValue) => void;
  filterType: QueryBuilderFilterType;
  clickId?: string;
  inputId?: string;
  isDisabled?: boolean;
  hasWarning?: boolean;
  isNewConditionalFormatting?: boolean;
  menuPortalTarget?: HTMLElement | null;
  renderInPortal?: boolean;
  locale: Locale;
  i18nLocale?: I18nLocale;
};
type HandleValueChangeArgs = {
  from?: string;
  to?: string;
};
type BetweenDatePickerInputProperties = {
  value?: QueryBuilderRuleValue;
  onValueChange: (value: QueryBuilderRuleValue) => void;
  inputId?: string;
  mode: RelativeDatePickerMode;
  isDisabled?: boolean;
  hasWarning?: boolean;
  presetMode?: RelativeDatePresetMode;
  isNewConditionalFormatting?: boolean;
  menuPortalTarget?: HTMLElement | null;
  renderInPortal?: boolean;
  locale: Locale;
  i18nLocale: I18nLocale;
};
const BetweenDatePickerInput = ({
  value,
  onValueChange,
  inputId,
  mode,
  isDisabled,
  hasWarning,
  presetMode,
  isNewConditionalFormatting,
  menuPortalTarget,
  renderInPortal = true,
  locale,
  i18nLocale,
}: BetweenDatePickerInputProperties) => {
  const [from, to] = Array.isArray(value)
    ? value.map(String)
    : [undefined, undefined];
  const handleValueChange = ({
    from: newFrom,
    to: newTo,
  }: HandleValueChangeArgs) => {
    onValueChange([
      newFrom !== undefined ? newFrom : (from ?? ''),
      newTo !== undefined ? newTo : (to ?? ''),
    ]);
  };

  if (isNewConditionalFormatting) {
    const fromDate = from ? parseDate(from) : null;
    const toDate = to ? parseDate(to) : null;

    return (
      <DateBetweenPicker
        locale={locale}
        startDatePicker={{
          date: fromDate,
          onChange: isoString => handleValueChange({ from: isoString || '' }),
          inputId: inputId,
          isDisabled,
          hasWarning,
          renderInPortal,
        }}
        endDatePicker={{
          date: toDate,
          onChange: isoString => handleValueChange({ to: isoString || '' }),
          inputId: `${inputId}-to`,
          isDisabled,
          hasWarning,
          renderInPortal,
        }}
      />
    );
  }

  return (
    <>
      <Flex flex={1}>
        <RelativeDatePicker
          value={from}
          onChange={value => handleValueChange({ from: value })}
          relativeDatePickerMode={mode}
          aria-label={t(
            i18nLocale,
            'dateTimePicker:selectFromDate',
            'Select from date'
          )}
          inputId={inputId}
          isDisabled={isDisabled}
          hasWarning={hasWarning}
          presetMode={presetMode}
          menuPortalTarget={menuPortalTarget}
        />
      </Flex>
      <Flex flex={1}>
        <RelativeDatePicker
          value={to}
          onChange={value => handleValueChange({ to: value })}
          relativeDatePickerMode={mode}
          aria-label={t(
            i18nLocale,
            'dateTimePicker:selectToDate',
            'Select to date'
          )}
          inputId={`${inputId}-to`}
          isDisabled={isDisabled}
          hasWarning={hasWarning}
          presetMode={presetMode}
          menuPortalTarget={menuPortalTarget}
        />
      </Flex>
    </>
  );
};

const toStringOrUndefined = (value?: QueryBuilderRuleValue) => {
  if (isString(value)) return value;
};

export const RuleDatePickerInput = ({
  value,
  operator,
  onValueChange,
  filterType,
  clickId,
  inputId,
  isDisabled,
  hasWarning,
  isNewConditionalFormatting,
  menuPortalTarget,
  renderInPortal,
  locale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: RuleDatePickerInputProps) => {
  const mode =
    filterType === QueryBuilderFilterType.DATE_TIME
      ? RelativeDatePickerMode.DATE_TIME
      : RelativeDatePickerMode.DATE;

  const valueKind = getDateValueKind(operator);

  if (valueKind === DateValueKind.RELATIVE) {
    return (
      <RelativeDateValue
        value={value as RelativeDateValueType | null | undefined}
        onValueChange={onValueChange}
        isDisabled={isDisabled}
        menuPortalTarget={menuPortalTarget}
        i18nLocale={i18nLocale}
      />
    );
  }

  if (operator === Operator.BETWEEN || valueKind === DateValueKind.PAIR) {
    return (
      <BetweenDatePickerInput
        value={value}
        onValueChange={value => onValueChange(value)}
        inputId={inputId}
        mode={mode}
        isDisabled={isDisabled}
        hasWarning={hasWarning}
        presetMode={
          isNewConditionalFormatting
            ? RelativeDatePresetMode.SHORT
            : RelativeDatePresetMode.NORMAL
        }
        isNewConditionalFormatting={isNewConditionalFormatting}
        menuPortalTarget={menuPortalTarget}
        renderInPortal={renderInPortal}
        locale={locale}
        i18nLocale={i18nLocale}
      />
    );
  }

  return (
    <Flex flex={1}>
      <RelativeDatePicker
        value={toStringOrUndefined(value)}
        onChange={value => onValueChange(value || '')}
        clickId={clickId}
        dataTestId="datepicker-input"
        relativeDatePickerMode={mode}
        inputId={inputId}
        isDisabled={isDisabled}
        hasWarning={hasWarning}
        presetMode={
          isNewConditionalFormatting
            ? RelativeDatePresetMode.SHORT
            : RelativeDatePresetMode.NORMAL
        }
        showIcon={isNewConditionalFormatting}
        menuPortalTarget={menuPortalTarget}
      />
    </Flex>
  );
};
